import { forwardRef, MouseEvent } from 'react';
import SpriteIcon from '../shared/sprite-icon';
import { useTranslation } from '../../hooks/use-translation';
import OWLink from '../shared/ow-link';
import useIsOverwolfIframe from '../../hooks/use-is-overwolf-iframe';
import DownloadInProgressLabel from './download-in-progress-label';

interface MainDownloadBtnProps {
  appTitle: string;
  btnLabel: string;
  url: string;
  className: string;
  icon?: string;
  isLoading?: boolean;
  // Electron downloads go through the shell's downloadApp (an installer file
  // download) rather than installApp, so the in-progress verb differs.
  isElectron?: boolean;
  handleClickDownload: (
    event: MouseEvent<HTMLElement>,
    overrideUrl?: string,
  ) => void;
}

export const MainDownloadBtn =
  forwardRef<HTMLAnchorElement, MainDownloadBtnProps>(
    ({
      appTitle,
      btnLabel,
      url,
      className,
      icon,
      isLoading = false,
      isElectron = false,
      handleClickDownload
    }, ref
    ) => {

      const { t } = useTranslation();
      const { isOverwolfIframe } = useIsOverwolfIframe();

      // Inside the Overwolf client, swap the label for a spinner + "Installing..."
      // / "Downloading..." instead of the striped background (see
      // `.is-downloading` in global-elements.scss), which is kept for the web's
      // modal-driven flow.
      const showInstallingState = isLoading && isOverwolfIframe;
      const btnClassName =
        isLoading && !isOverwolfIframe ? `${className} is-downloading` : className;

      return (
        <OWLink
          href={url}
          onClick={(event: MouseEvent<HTMLElement>) =>
            handleClickDownload(event, url)
          }
          className={btnClassName}
          download={t('appInstaller', { title: appTitle })}
          ref={ref}
        >
          {showInstallingState ? (
            <DownloadInProgressLabel isElectron={isElectron} />
          ) : (
            <>
              {icon && (
                <svg>
                  <SpriteIcon svgClassName='icon' icon={icon.toLowerCase()} />
                </svg>
              )}
              {btnLabel}
            </>
          )}
        </OWLink>
      );
    }
  );
